import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  LuLoaderCircle,
  LuEllipsis,
  LuPencil,
  LuShieldCheck,
  LuTrash2,
  LuTriangleAlert,
  LuUsers,
  LuX,
} from "react-icons/lu";

import DeleteConfirmationDialog from "components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import ThemedSelect from "components/FormControls/ThemedSelect";
import ListingActionMenu from "components/ListingActionMenu";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import { Skeleton } from "components/Skeleton";
import {
  addBuRole,
  addExistingTeamMember,
  addUserData,
  deleteUserRecords,
  fetchRolesData,
  fetchBURoleByID,
  fetchReusableTeamMembers,
  fetchUser,
  updateBuRole,
  updateUserData,
} from "utils/api";
import { deleteRoleRecords } from "utils/superAdminApi";
import { appConstants } from "utils/appConstants";
import { setAuthSessionItem } from "utils/authSession";
import calculateRoleRights from "utils/roleRightPermissions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ACCESS_OPTIONS = ["None", "View Only", "Full Access"];

export const refreshActiveRolePermissions = ({
  roleID,
  permissions,
  reload = () => window.location.replace("/dashboard"),
}) => {
  const login = readSession("LOGIN_DETAILS", {});
  const activeRole = readSession("ROLE", {});
  const activeRoleID = login.roleID || activeRole._id;
  if (!roleID || String(activeRoleID || "") !== String(roleID)) return false;

  setAuthSessionItem("ROLE_RIGHTS_LOGIN", JSON.stringify(permissions || []));
  calculateRoleRights();
  reload();
  return true;
};
const ROLE_MODULES = [
  {
    label: "Dashboard",
    matches: (permission) =>
      /dashboard/i.test(
        `${permission.menuName || ""} ${permission.screenName || ""}`
      ),
  },
  {
    label: "Quotations",
    matches: (permission) =>
      /quotation/i.test(permission.menuName || permission.screenName || "") &&
      !/report/i.test(permission.screenName || ""),
  },
  {
    label: "Invoices",
    matches: (permission) =>
      /invoice/i.test(permission.menuName || permission.screenName || "") &&
      !/report|reminder/i.test(permission.screenName || ""),
  },
  {
    label: "Credit notes",
    matches: (permission) =>
      /credit notes?/i.test(permission.menuName || permission.screenName || ""),
  },
  {
    label: "RCTI",
    matches: (permission) =>
      /^rcti$/i.test(permission.menuName || permission.screenName || ""),
  },
  {
    label: "Expenses",
    matches: (permission) =>
      /^expenses?$/i.test(permission.menuName || permission.screenName || ""),
  },
  {
    label: "Debit notes",
    matches: (permission) =>
      /debit notes?/i.test(permission.menuName || permission.screenName || ""),
  },
  {
    label: "Debt collection",
    matches: (permission) =>
      /debt collection/i.test(permission.menuName || "") ||
      /^debts$/i.test(permission.screenName || ""),
  },
  {
    label: "Contacts",
    matches: (permission) =>
      /^contacts?$/i.test(permission.menuName || permission.screenName || ""),
  },
  {
    label: "Items",
    matches: (permission) =>
      /^items$/i.test(permission.screenName || permission.menuName || ""),
  },
  {
    label: "Reports",
    matches: (permission) =>
      /report/i.test(
        `${permission.menuName || ""} ${permission.screenName || ""}`
      ),
  },
  {
    label: "Settings",
    matches: (permission) =>
      (/settings/i.test(permission.menuName || "") &&
        !/^items$/i.test(permission.screenName || "")) ||
      /default setting|subscriptions|my users|user roles/i.test(
        permission.screenName || ""
      ),
  },
];

const moduleAccessValue = (permissions) => {
  const values = new Set(
    permissions.map((permission) => permission.screenRight || "None")
  );
  if (values.size === 1) return [...values][0];
  if (values.has("Full Access")) return "Full Access";
  if (values.has("View Only")) return "View Only";
  return "None";
};
const readSession = (key, fallback) => {
  try {
    return JSON.parse(sessionStorage.getItem(key)) || fallback;
  } catch (_) {
    return fallback;
  }
};

const getName = (member) =>
  member?.fullNameString?.fullName ||
  `${member?.firstName || ""} ${member?.lastName || ""}`.trim() ||
  member?.emailAddress ||
  "Team member";

const initials = (value) =>
  String(value || "IR")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

function TeamTableSkeleton({ pending = false }) {
  const fields = pending
    ? ["Email", "Role", "Status"]
    : ["Name", "Email", "Role", "Status", "Actions"];
  return Array.from({ length: pending ? 3 : 5 }, (_, row) => (
    <tr key={row} aria-hidden="true">
      {fields.map((field) => (
        <td
          key={field}
          data-label={field}
          className={field === "Actions" ? "settings-action-cell" : undefined}
        >
          {field === "Name" ? (
            <span className="settings-member-name">
              <i>
                <Skeleton variant="circular" width={26} height={26} />
              </i>
              <Skeleton width="65%" height={14} />
            </span>
          ) : (
            <Skeleton
              width={field === "Actions" ? 24 : field === "Status" ? 68 : "78%"}
              height={field === "Actions" ? 24 : field === "Status" ? 22 : 14}
              radius={field === "Actions" || field === "Status" ? 999 : 4}
            />
          )}
        </td>
      ))}
    </tr>
  ));
}

function RoleTableSkeleton() {
  return Array.from({ length: 5 }, (_, row) => (
    <tr key={row} aria-hidden="true">
      <td data-label="Role name">
        <Skeleton width="58%" height={14} />
      </td>
      <td data-label="Assigned members">
        <Skeleton width="42%" height={14} />
      </td>
      <td data-label="Type">
        <Skeleton width={68} height={22} radius={999} />
      </td>
      <td data-label="Actions" className="settings-action-cell">
        <Skeleton width={24} height={24} radius={999} />
      </td>
    </tr>
  ));
}

function TeamTabs({ view, onChange, disabled = false }) {
  return (
    <div
      className="settings-team-tabs"
      role="tablist"
      aria-label="Team settings"
    >
      <button
        type="button"
        role="tab"
        disabled={disabled}
        aria-selected={view === "members"}
        className={view === "members" ? "active" : ""}
        onClick={() => onChange("members")}
      >
        Members
      </button>
      <button
        type="button"
        role="tab"
        disabled={disabled}
        aria-selected={view === "roles"}
        className={view === "roles" ? "active" : ""}
        onClick={() => onChange("roles")}
      >
        Roles &amp; permissions
      </button>
    </div>
  );
}

export default function TeamSettings() {
  const dispatch = useDispatch();
  const path = window.location.pathname;
  const routeId = path.split("/").filter(Boolean)[2] || "";
  const startsInRoles = path.startsWith("/myroles");
  const startsInNew = path.endsWith("/new");
  const startsInEdit = path.includes("/edit/");
  const sessionUser = useMemo(() => readSession("LOGIN_DETAILS", {}), []);
  const planDetails = useMemo(() => readSession("PLAN_DETAILS", []), []);
  const businessUserID = sessionUser.businessUserID || sessionUser._id;
  const sessionOrganisationName =
    sessionUser.companyName ||
    sessionUser.businessName ||
    sessionUser.organisationName ||
    sessionUser.organizationName ||
    (Array.isArray(planDetails)
      ? planDetails[0]?.companyName
      : planDetails?.companyName) ||
    "the selected organisation";
  const [currentOrganisationName, setCurrentOrganisationName] = useState(
    sessionOrganisationName
  );
  const [view, setView] = useState(startsInRoles ? "roles" : "members");
  const [mode, setMode] = useState(
    startsInNew ? (startsInRoles ? "create-role" : "member-choice") : "list"
  );
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [menuId, setMenuId] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const roleNameRef = useRef(null);
  const roleSavingRef = useRef(false);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleDetailsLoading, setRoleDetailsLoading] = useState(false);
  const [roleDeleting, setRoleDeleting] = useState(false);
  const [invite, setInvite] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roleID: "",
  });
  const [existingMembers, setExistingMembers] = useState([]);
  const [existingMemberID, setExistingMemberID] = useState("");
  const [existingMemberRoleID, setExistingMemberRoleID] = useState("");
  const [existingMembersLoading, setExistingMembersLoading] = useState(false);
  const [memberRoleID, setMemberRoleID] = useState("");
  const [memberStatus, setMemberStatus] = useState("Active");
  const [memberName, setMemberName] = useState({ firstName: "", lastName: "" });
  const [roleForm, setRoleForm] = useState(() => ({
    roleName: "",
    roleDescription: "",
    permissions:
      startsInRoles && startsInNew
        ? readSession("ROLE_RIGHTS_LOGIN", []).map((permission) => ({
            ...permission,
            _id: undefined,
            screenRight: "None",
          }))
        : [],
  }));
  const roleModules = ROLE_MODULES.map((module) => ({
    ...module,
    entries: roleForm.permissions
      .map((permission, index) => ({ permission, index }))
      .filter(({ permission }) => module.matches(permission)),
  }));

  const notify = (type, title, message = "") => {
    showDraftSavedMessage(message || title, type, title);
  };

  const loadRoles = useCallback(() => {
    setRolesLoading(true);
    fetchRolesData(
      { businessUserID, userID: sessionUser._id },
      (data) => {
        setRoles(Array.isArray(data?.Result_Output) ? data.Result_Output : []);
        setRolesLoading(false);
      },
      () => {
        setRolesLoading(false);
        notify(
          "error",
          "Roles could not be loaded",
          "Check your connection and try again."
        );
      }
    );
  }, [businessUserID, sessionUser._id]);

  const loadMembers = useCallback(() => {
    setLoading(true);
    fetchUser(
      {
        businessUserID,
        userID: sessionUser._id,
        userStatus: "All",
        page: 1,
        limit: 500,
        filterValues: "",
        sortingType: "",
        sortingField: "",
        searchParameterList: [
          "fullNameString.fullName",
          "emailAddress",
          "role",
          "userStatus",
        ],
      },
      (data) => {
        setMembers(data?.Result_Output?.docs || []);
        setLoading(false);
      },
      dispatch
    );
  }, [businessUserID, dispatch, sessionUser._id]);

  useEffect(() => {
    loadMembers();
    loadRoles();
  }, [loadMembers, loadRoles]);

  useEffect(() => {
    if (!startsInEdit || !routeId) return;
    if (startsInRoles) {
      const role = roles.find((item) => item._id === routeId);
      if (role) openRoleForm(role);
      return;
    }
    const member = members.find((item) => item._id === routeId);
    if (member) openMemberEditor(member);
    // Route-derived edit state only needs to initialise after its record loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, roles, routeId, startsInEdit, startsInRoles]);

  const pending = members.filter((member) =>
    /pending|invited/i.test(member.userStatus || "")
  );
  const active = members.filter(
    (member) => (member.membershipStatus || member.userStatus) === "Active"
  );

  const openList = (nextView = view) => {
    setView(nextView);
    setMode("list");
    setMenuId("");
    setSelectedMember(null);
    setSelectedRole(null);
    setFormError("");
    setFieldErrors({});
  };

  const submitInvite = () => {
    const errors = {};
    if (!invite.firstName.trim()) errors.firstName = "Enter a first name.";
    if (!EMAIL_RE.test(invite.email.trim()))
      errors.email = "Enter a valid email address.";
    if (!invite.roleID) errors.roleID = "Select a role.";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    addUserData(
      {
        firstName: invite.firstName.trim(),
        lastName: invite.lastName.trim(),
        emailAddress: invite.email.trim(),
        roleID: invite.roleID,
        userStatus: "Active",
        userType: "Business",
        userKey: "ChildUser",
        businessUserID,
        userID: sessionUser._id,
        loginURL: appConstants.loginURL,
      },
      (data) => {
        if (!data?.Result_Status) {
          notify(
            "error",
            "Invitation failed",
            data?.Result_Message || "The invitation could not be sent."
          );
          return;
        }
        setInvite({ firstName: "", lastName: "", email: "", roleID: "" });
        const existingTeamMember = Boolean(
          data?.Result_Output?.existingTeamMember
        );
        notify(
          "success",
          existingTeamMember ? "Organisation access added" : "Invitation sent",
          existingTeamMember
            ? `${invite.email} can now access this organisation using their existing login.`
            : `An invitation with a temporary password was sent to ${invite.email}.`
        );
        loadMembers();
        openList("members");
      },
      dispatch
    );
  };

  const openExistingMemberForm = () => {
    setMode("add-existing");
    setExistingMemberID("");
    setExistingMemberRoleID("");
    setFieldErrors({});
    setFormError("");
    setExistingMembersLoading(true);
    fetchReusableTeamMembers(
      { businessUserID },
      (data) => {
        const output = data?.Result_Output;
        setExistingMembers(
          Array.isArray(output) ? output : output?.members || []
        );
        if (output?.organisationName)
          setCurrentOrganisationName(output.organisationName);
        setExistingMembersLoading(false);
      },
      (error) => {
        setExistingMembersLoading(false);
        notify(
          "error",
          "Existing members could not be loaded",
          error?.response?.data?.Result_Message ||
            "Existing members could not be loaded."
        );
      }
    );
  };

  const submitExistingMember = () => {
    const errors = {};
    if (!existingMemberID) errors.memberUserID = "Select a team member.";
    if (!existingMemberRoleID) errors.roleID = "Select a role.";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    addExistingTeamMember(
      {
        businessUserID,
        memberUserID: existingMemberID,
        roleID: existingMemberRoleID,
      },
      (data) => {
        if (!data?.Result_Status) {
          notify(
            "error",
            "Member could not be added",
            data?.Result_Message || "The member could not be added."
          );
          return;
        }
        const selected = existingMembers.find(
          (member) => member._id === existingMemberID
        );
        notify(
          "success",
          "Organisation access added",
          `${
            selected?.emailAddress || "The member"
          } can now access this organisation using their existing login.`
        );
        loadMembers();
        openList("members");
      },
      dispatch,
      (error) =>
        notify(
          "error",
          "Member could not be added",
          error?.response?.data?.Result_Message ||
            "The member could not be added."
        )
    );
  };

  const saveMemberRole = () => {
    if (!memberName.firstName.trim()) {
      setFieldErrors({ firstName: "Enter a first name." });
      return;
    }
    if (!memberRoleID) {
      setFieldErrors({ roleID: "Select a role." });
      return;
    }
    updateUserData(
      {
        ...selectedMember,
        _id: selectedMember._id,
        roleID: memberRoleID,
        membershipStatus: memberStatus,
        firstName: memberName.firstName.trim(),
        lastName: memberName.lastName.trim(),
      },
      (data) => {
        if (!data?.Result_Status) {
          notify(
            "error",
            "Role update failed",
            data?.Result_Message || "Try again."
          );
          return;
        }
        notify(
          "success",
          "Member role updated",
          `${getName(selectedMember)} now has the selected role.`
        );
        loadMembers();
        openList("members");
      },
      dispatch,
      (error) =>
        notify(
          "error",
          "Member update failed",
          error?.response?.data?.Result_Message || "Try again."
        )
    );
  };

  const removeMember = () => {
    deleteUserRecords(
      { _id: [selectedMember._id], businessUserID, userID: sessionUser._id },
      (data) => {
        setConfirm(null);
        if (!data?.Result_Status) {
          notify(
            "error",
            "Member removal failed",
            data?.Result_Message || "Try again."
          );
          return;
        }
        notify(
          "success",
          "Member removed",
          `${getName(selectedMember)} no longer has workspace access.`
        );
        loadMembers();
        openList("members");
      },
      dispatch
    );
  };

  const permissionSource = (role) => {
    const existing = role?.permission || role?.roleRights;
    if (Array.isArray(existing) && existing.length) return existing;
    if (role) return [];
    return readSession("ROLE_RIGHTS_LOGIN", []).map((item) => ({
      ...item,
      _id: undefined,
      screenRight: item.screenRight || "None",
    }));
  };

  const openRoleForm = (role = null) => {
    if (
      role &&
      (role.protectedRoleKey === "OWNER" || role.defaultPlanRole === "Yes")
    ) {
      openList("roles");
      return;
    }
    const selectedPermissions = role ? permissionSource(role) : [];
    setSelectedRole(role);
    setRoleForm({
      roleName: role?.roleName || "",
      roleDescription: role?.roleDescription || "",
      permissions: role
        ? selectedPermissions
        : permissionSource(null).map((p) => ({ ...p, screenRight: "None" })),
    });
    setMode(role ? "edit-role" : "create-role");
    setFieldErrors({});
    setFormError("");
    const needsPermissionRequest = Boolean(role && !selectedPermissions.length);
    setRoleDetailsLoading(needsPermissionRequest);
    if (needsPermissionRequest) {
      fetchBURoleByID(
        { _id: role._id },
        (data) => {
          const permissions = Array.isArray(data?.Result_Output)
            ? data.Result_Output
            : [];
          setRoleDetailsLoading(false);
          if (!data?.Result_Status || !permissions.length) {
            notify(
              "error",
              "Role permissions could not be loaded",
              data?.Result_Message || "Refresh and try again."
            );
            return;
          }
          setRoleForm({
            roleName: permissions[0]?.roleName || role.roleName || "",
            roleDescription:
              permissions[0]?.roleDescription || role.roleDescription || "",
            permissions,
          });
        },
        dispatch,
        (error) => {
          setRoleDetailsLoading(false);
          notify(
            "error",
            "Role permissions could not be loaded",
            error?.response?.data?.Result_Message || "Refresh and try again."
          );
        }
      );
    }
  };

  const reportRoleNameError = (message) => {
    setFieldErrors((current) => ({ ...current, roleName: message }));
  };

  useEffect(() => {
    if (!fieldErrors.roleName || roleSaving) return;
    roleNameRef.current?.focus({ preventScroll: true });
    roleNameRef.current?.scrollIntoView({
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "center",
    });
  }, [fieldErrors, roleSaving]);

  const saveRole = () => {
    if (roleSavingRef.current) return;
    const name = roleForm.roleName.trim();
    if (!name) {
      reportRoleNameError("Enter a role name.");
      return;
    }
    if (
      roles.some(
        (role) =>
          role.roleName?.trim().toLowerCase() === name.toLowerCase() &&
          role._id !== selectedRole?._id
      )
    ) {
      reportRoleNameError("A role with this name already exists.");
      return;
    }
    const values = {
      ...(selectedRole?._id ? { _id: selectedRole._id } : {}),
      roleName: name,
      roleDescription: roleForm.roleDescription.trim(),
      businessUserID,
      userID: sessionUser._id,
    };
    const api = selectedRole ? updateBuRole : addBuRole;
    setFieldErrors({});
    setFormError("");
    roleSavingRef.current = true;
    setRoleSaving(true);
    const finishSaving = () => {
      roleSavingRef.current = false;
      setRoleSaving(false);
    };
    api(
      { values, permission: roleForm.permissions },
      (data) => {
        finishSaving();
        if (!data?.Result_Status) {
          if (
            data?.Result_Error?.field === "roleName" ||
            /^Role already exists\.?$/i.test(data?.Result_Message || "")
          ) {
            reportRoleNameError(data.Result_Message);
            return;
          }
          notify(
            "error",
            selectedRole ? "Role update failed" : "Role creation failed",
            data?.Result_Message || "Try again."
          );
          return;
        }
        if (selectedRole) {
          notify("success", "Role updated", `${name} is ready to assign.`);
        } else {
          notify("success", "Role created", `${name} is ready to assign.`);
        }
        if (
          selectedRole &&
          refreshActiveRolePermissions({
            roleID: selectedRole._id,
            permissions: roleForm.permissions,
          })
        )
          return;
        loadRoles();
        openList("roles");
      },
      dispatch,
      (error) => {
        finishSaving();
        notify(
          "error",
          selectedRole ? "Role update failed" : "Role creation failed",
          error?.response?.data?.Result_Message ||
            "We could not confirm the save. Check your connection and try again."
        );
      }
    );
  };

  const removeRole = () => {
    if (roleDeleting || !selectedRole?._id) return;
    setRoleDeleting(true);
    deleteRoleRecords(
      { _id: selectedRole._id, userID: sessionUser._id },
      (data) => {
        setRoleDeleting(false);
        setConfirm(null);
        if (!data?.Result_Status) {
          notify(
            "error",
            "Role deletion failed",
            data?.Result_Message || "This role may still be assigned."
          );
          return;
        }
        loadRoles();
        openList("roles");
      },
      dispatch,
      (error) => {
        setRoleDeleting(false);
        setConfirm(null);
        notify(
          "error",
          "Role deletion failed",
          error?.response?.data?.Result_Message || "Try again."
        );
      }
    );
  };

  const openMemberEditor = (member) => {
    setSelectedMember(member);
    const role = roles.find((item) => item.roleName === member.role);
    setMemberRoleID(role?._id || "");
    setMemberStatus(member.membershipStatus || member.userStatus || "Active");
    setMemberName({
      firstName: member.firstName || "",
      lastName: member.lastName || "",
    });
    setMode("edit-member");
    setMenuId("");
  };

  const title =
    mode === "member-choice"
      ? "Add member"
      : mode === "invite"
      ? "Invite member"
      : mode === "add-existing"
      ? "Add existing member"
      : mode === "edit-member"
      ? "Edit member"
      : mode === "create-role"
      ? "Create role"
      : mode === "edit-role"
      ? "Edit role"
      : view === "roles"
      ? "Roles & permissions"
      : "Team";
  const description =
    mode === "member-choice"
      ? "Choose whether to invite someone new or add a member who already has a login."
      : mode === "invite"
      ? "Send an invitation to someone who needs access to your workspace."
      : mode === "add-existing"
      ? "Assign an existing team member to the currently selected organisation."
      : mode === "edit-member"
      ? "Change this member’s workspace role."
      : /role/.test(mode)
      ? "Create a custom role and choose access for each module."
      : view === "roles"
      ? "Built-in roles are protected. Create and edit custom roles."
      : "Manage who can access your Invoice & Recover workspace.";

  return (
    <div className="settings-team-workspace">
      <header className="settings-section-heading settings-heading-action">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        {mode === "list" && (
          <button
            className="settings-button primary"
            type="button"
            onClick={() =>
              view === "roles" ? openRoleForm() : setMode("member-choice")
            }
          >
            {view === "roles" ? "Create role" : "Add member"}
          </button>
        )}
      </header>
      {!["invite", "member-choice", "add-existing"].includes(mode) && (
        <TeamTabs view={view} onChange={openList} disabled={roleSaving} />
      )}

      {mode === "member-choice" && (
        <section className="settings-team-form-card settings-member-choice">
          <button type="button" onClick={() => setMode("invite")}>
            <LuUsers />
            <strong>Invite new member</strong>
            <span>Create first-time access and send an invitation email.</span>
          </button>
          <button type="button" onClick={openExistingMemberForm}>
            <LuShieldCheck />
            <strong>Add existing member</strong>
            <span>
              Reuse a team member’s current login without another invitation.
            </span>
          </button>
          <footer>
            <button
              type="button"
              className="settings-button secondary"
              onClick={() => openList("members")}
            >
              Cancel
            </button>
          </footer>
        </section>
      )}

      {mode === "list" && view === "members" && (
        <>
          <div className="settings-team-stats">
            {loading ? (
              [0, 1].map((card) => (
                <section
                  key={card}
                  className="settings-team-stat-skeleton"
                  aria-hidden="true"
                >
                  <span>
                    <Skeleton width="46%" height={13} radius={4} />
                  </span>
                  <strong>
                    <Skeleton width="22%" radius={6} />
                  </strong>
                  <small>
                    <Skeleton width="58%" height={12} radius={4} />
                  </small>
                </section>
              ))
            ) : (
              <>
                <section>
                  <span>Active members</span>
                  <strong>{active.length}</strong>
                  <small>Across your workspace</small>
                </section>
                <section>
                  <span>Pending invitations</span>
                  <strong>{pending.length}</strong>
                  <small>Awaiting response</small>
                </section>
              </>
            )}
          </div>
          <section className="settings-team-table-card">
            <header>
              <h3>Members</h3>
              <span>{active.length} members</span>
            </header>
            <div className="settings-team-table-scroll">
              <table aria-busy={loading}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th className="settings-action-heading">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <TeamTableSkeleton />
                  ) : (
                    members.map((member) => (
                      <tr key={member._id}>
                        <td data-label="Name">
                          <span className="settings-member-name">
                            <i>{initials(getName(member))}</i>
                            {getName(member)}
                          </span>
                        </td>
                        <td data-label="Email">{member.emailAddress}</td>
                        <td data-label="Role">{member.role || "Member"}</td>
                        <td data-label="Status">
                          <span
                            className={`settings-status ${(
                              member.membershipStatus ||
                              member.userStatus ||
                              "Active"
                            ).toLowerCase()}`}
                          >
                            {member.membershipStatus ||
                              member.userStatus ||
                              "Active"}
                          </span>
                        </td>
                        <td
                          className="settings-action-cell"
                          data-label="Actions"
                        >
                          <button
                            type="button"
                            className="listing-action-trigger"
                            aria-label={`Actions for ${getName(member)}`}
                            aria-expanded={menuId === member._id}
                            onClick={(event) => {
                              const isOpen = menuId === member._id;
                              setMenuId(isOpen ? "" : member._id);
                              setMenuAnchorEl(
                                isOpen ? null : event.currentTarget
                              );
                            }}
                          >
                            <LuEllipsis />
                          </button>
                          {menuId === member._id && (
                            <ListingActionMenu
                              anchorEl={menuAnchorEl}
                              label={`Actions for ${getName(member)}`}
                              onClose={() => {
                                setMenuId("");
                                setMenuAnchorEl(null);
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => openMemberEditor(member)}
                              >
                                <LuPencil />
                                Edit role
                              </button>
                              <button
                                type="button"
                                className="danger"
                                onClick={() => {
                                  setSelectedMember(member);
                                  setConfirm("member");
                                  setMenuId("");
                                }}
                              >
                                <LuTrash2 />
                                Remove member
                              </button>
                            </ListingActionMenu>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                  {!loading && !active.length && (
                    <tr>
                      <td colSpan="5" className="settings-team-empty-row">
                        <LuUsers />
                        No active members
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          <section className="settings-team-table-card">
            <header>
              <h3>Pending invitations</h3>
              <span>{pending.length} pending</span>
            </header>
            {loading ? (
              <div className="settings-team-table-scroll">
                <table aria-busy="true">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TeamTableSkeleton pending />
                  </tbody>
                </table>
              </div>
            ) : pending.length ? (
              <div className="settings-team-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((member) => (
                      <tr key={member._id}>
                        <td data-label="Email">{member.emailAddress}</td>
                        <td data-label="Role">{member.role || "Member"}</td>
                        <td data-label="Status">
                          <span className="settings-status pending">
                            Pending
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="settings-team-empty-pending">
                <LuUsers />
                <strong>No pending invitations</strong>
                <p>New invitations awaiting a response will appear here.</p>
              </div>
            )}
          </section>
        </>
      )}

      {mode === "invite" && (
        <section className="settings-team-form-card">
          <h3>Invitation details</h3>
          {formError && <div className="settings-form-alert">{formError}</div>}
          <div className="settings-role-fields">
            <label>
              First name <b>*</b>
              <input
                className={fieldErrors.firstName ? "error" : ""}
                value={invite.firstName}
                onChange={(e) =>
                  setInvite({ ...invite, firstName: e.target.value })
                }
                placeholder="First name"
              />
              <small>{fieldErrors.firstName}</small>
            </label>
            <label>
              Last name (optional)
              <input
                value={invite.lastName}
                onChange={(e) =>
                  setInvite({ ...invite, lastName: e.target.value })
                }
                placeholder="Last name"
              />
              <small>Optional</small>
            </label>
          </div>
          <label>
            Email address <b>*</b>
            <input
              className={fieldErrors.email ? "error" : ""}
              value={invite.email}
              onChange={(e) => setInvite({ ...invite, email: e.target.value })}
              placeholder="name@company.com"
            />
            <small>{fieldErrors.email || "Use a work email address"}</small>
          </label>
          <label>
            Role <b>*</b>
            <ThemedSelect
              className="settings-role-permission-select"
              aria-label="Role"
              aria-invalid={Boolean(fieldErrors.roleID)}
              aria-describedby="team-invite-role-hint"
              menuPortalContainerSelector=".settings-page"
              value={invite.roleID}
              onChange={(e) => setInvite({ ...invite, roleID: e.target.value })}
            >
              <option value="">Select a role</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.roleName}
                </option>
              ))}
            </ThemedSelect>
            <small id="team-invite-role-hint">
              {fieldErrors.roleID || "Choose the role for this team member"}
            </small>
          </label>
          <p>
            This creates a new team login and sends an invitation email. To
            reuse someone’s current login, choose Add existing member instead.
          </p>
          <footer>
            <button
              type="button"
              className="settings-button secondary"
              onClick={() => openList("members")}
            >
              Cancel
            </button>
            <button
              type="button"
              className="settings-button primary"
              onClick={submitInvite}
            >
              Send invitation
            </button>
          </footer>
        </section>
      )}

      {mode === "add-existing" && (
        <section className="settings-team-form-card">
          <h3>Add existing member</h3>
          <p className="settings-existing-destination">
            Adding to <strong>{currentOrganisationName}</strong>. No invitation
            email will be sent.
          </p>
          {formError && <div className="settings-form-alert">{formError}</div>}
          {existingMembersLoading ? (
            <div
              className="settings-existing-member-loading"
              aria-label="Loading existing members"
            >
              <Skeleton height={40} radius={8} />
              <Skeleton height={40} radius={8} />
            </div>
          ) : existingMembers.length ? (
            <>
              <label>
                Team member <b>*</b>
                <ThemedSelect
                  aria-label="Existing team member"
                  value={existingMemberID}
                  onChange={(event) => {
                    setExistingMemberID(event.target.value);
                    setFieldErrors((current) => ({
                      ...current,
                      memberUserID: undefined,
                    }));
                  }}
                >
                  <option value="">Select a team member</option>
                  {existingMembers.map((member) => (
                    <option key={member._id} value={member._id}>
                      {`${member.firstName || ""} ${
                        member.lastName || ""
                      }`.trim() || member.emailAddress}{" "}
                      · {member.sourceOrganisation}
                    </option>
                  ))}
                </ThemedSelect>
                <small>{fieldErrors.memberUserID}</small>
              </label>
              <label>
                Role in {currentOrganisationName} <b>*</b>
                <ThemedSelect
                  aria-label="Role for existing member"
                  value={existingMemberRoleID}
                  onChange={(event) => {
                    setExistingMemberRoleID(event.target.value);
                    setFieldErrors((current) => ({
                      ...current,
                      roleID: undefined,
                    }));
                  }}
                >
                  <option value="">Select a role</option>
                  {roles.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.roleName}
                    </option>
                  ))}
                </ThemedSelect>
                <small>{fieldErrors.roleID}</small>
              </label>
            </>
          ) : (
            <div className="settings-team-empty-pending">
              <LuUsers />
              <strong>No existing members available</strong>
              <p>Members from your other organisations will appear here.</p>
            </div>
          )}
          <footer>
            <button
              type="button"
              className="settings-button secondary"
              onClick={() => setMode("member-choice")}
            >
              Back
            </button>
            <button
              type="button"
              className="settings-button primary"
              disabled={existingMembersLoading || !existingMembers.length}
              onClick={submitExistingMember}
            >
              Add to organisation
            </button>
          </footer>
        </section>
      )}

      {mode === "edit-member" && (
        <section className="settings-team-form-card settings-member-editor">
          <h3>Member details</h3>
          <div className="settings-member-summary">
            <i>{initials(getName(selectedMember))}</i>
            <div>
              <strong>{getName(selectedMember)}</strong>
              <span>{selectedMember?.emailAddress}</span>
            </div>
          </div>
          <div className="settings-role-fields">
            <label>
              First name <b>*</b>
              <input
                className={fieldErrors.firstName ? "error" : ""}
                value={memberName.firstName}
                onChange={(e) => {
                  setMemberName({ ...memberName, firstName: e.target.value });
                  setFieldErrors((current) => ({
                    ...current,
                    firstName: undefined,
                  }));
                }}
                placeholder="First name"
              />
              <small>{fieldErrors.firstName}</small>
            </label>
            <label>
              Last name (optional)
              <input
                value={memberName.lastName}
                onChange={(e) =>
                  setMemberName({ ...memberName, lastName: e.target.value })
                }
                placeholder="Last name"
              />
            </label>
          </div>
          <div
            className="settings-role-fields settings-member-access-fields"
            data-testid="member-access-fields"
          >
            <label>
              Role <b>*</b>
              <ThemedSelect
                className="settings-role-permission-select"
                aria-label="Role"
                aria-invalid={Boolean(fieldErrors.roleID)}
                aria-describedby="team-member-role-hint"
                menuPortalContainerSelector=".settings-page"
                value={memberRoleID}
                onChange={(e) => setMemberRoleID(e.target.value)}
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.roleName}
                  </option>
                ))}
              </ThemedSelect>
              <small id="team-member-role-hint">
                {fieldErrors.roleID || "Role changes take effect immediately."}
              </small>
            </label>
            <label>
              Status <b>*</b>
              <ThemedSelect
                className="settings-role-permission-select"
                aria-label="Status"
                menuPortalContainerSelector=".settings-page"
                value={memberStatus}
                onChange={(e) => setMemberStatus(e.target.value)}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </ThemedSelect>
              <small>
                Inactive members temporarily lose access to this organisation.
              </small>
            </label>
          </div>
          <footer>
            <button
              type="button"
              className="settings-button secondary"
              onClick={() => openList("members")}
            >
              Cancel
            </button>
            <button
              type="button"
              className="settings-button primary"
              onClick={saveMemberRole}
            >
              Save changes
            </button>
          </footer>
        </section>
      )}

      {mode === "list" && view === "roles" && (
        <section className="settings-team-table-card">
          <header>
            <h3>Roles</h3>
            <span>
              {rolesLoading ? "Loading roles..." : `${roles.length} roles`}
            </span>
          </header>
          <div className="settings-team-table-scroll">
            <table aria-busy={rolesLoading}>
              <thead>
                <tr>
                  <th>Role name</th>
                  <th>Assigned members</th>
                  <th>Type</th>
                  <th className="settings-action-heading">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rolesLoading ? (
                  <RoleTableSkeleton />
                ) : (
                  roles.map((role) => {
                    const assigned = members.filter(
                      (member) => member.role === role.roleName
                    ).length;
                    const builtIn =
                      role.protectedRoleKey === "OWNER" ||
                      role.defaultPlanRole === "Yes";
                    return (
                      <tr key={role._id}>
                        <td data-label="Role name">{role.roleName}</td>
                        <td data-label="Assigned members">
                          {assigned} {assigned === 1 ? "member" : "members"}
                        </td>
                        <td data-label="Type">
                          {builtIn ? "Built-in" : "Custom"}
                        </td>
                        <td
                          className="settings-action-cell"
                          data-label="Actions"
                        >
                          {builtIn ? (
                            "—"
                          ) : (
                            <>
                              <button
                                type="button"
                                className="listing-action-trigger"
                                aria-label={`Actions for ${role.roleName}`}
                                aria-expanded={menuId === role._id}
                                onClick={(event) => {
                                  const isOpen = menuId === role._id;
                                  setMenuId(isOpen ? "" : role._id);
                                  setMenuAnchorEl(
                                    isOpen ? null : event.currentTarget
                                  );
                                }}
                              >
                                <LuEllipsis />
                              </button>
                              {menuId === role._id && (
                                <ListingActionMenu
                                  anchorEl={menuAnchorEl}
                                  label={`Actions for ${role.roleName}`}
                                  onClose={() => {
                                    setMenuId("");
                                    setMenuAnchorEl(null);
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => openRoleForm(role)}
                                  >
                                    <LuPencil />
                                    Edit role
                                  </button>
                                  <button
                                    type="button"
                                    className="danger"
                                    onClick={() => {
                                      setSelectedRole(role);
                                      setConfirm("role");
                                      setMenuId("");
                                    }}
                                  >
                                    <LuTrash2 />
                                    Delete role
                                  </button>
                                </ListingActionMenu>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
                {!rolesLoading && !roles.length && (
                  <tr>
                    <td colSpan="4" className="settings-team-empty-row">
                      <LuShieldCheck />
                      No roles available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {(mode === "create-role" || mode === "edit-role") && (
        <section className="settings-team-form-card settings-role-form">
          <fieldset
            className="settings-role-form-body"
            disabled={roleSaving || roleDetailsLoading}
            aria-busy={roleSaving || roleDetailsLoading}
          >
            <h3>Role details</h3>
            <div className="settings-role-fields">
              <label>
                Role name <b>*</b>
                <input
                  ref={roleNameRef}
                  className={fieldErrors.roleName ? "error" : ""}
                  aria-invalid={Boolean(fieldErrors.roleName)}
                  aria-describedby="team-role-name-error"
                  value={roleForm.roleName}
                  onChange={(e) => {
                    setRoleForm({ ...roleForm, roleName: e.target.value });
                    setFieldErrors((current) => ({
                      ...current,
                      roleName: undefined,
                    }));
                  }}
                  placeholder="Enter a role name"
                />
                <small
                  id="team-role-name-error"
                  role={fieldErrors.roleName ? "alert" : undefined}
                >
                  {fieldErrors.roleName}
                </small>
              </label>
              <label>
                Role description (optional)
                <input
                  value={roleForm.roleDescription}
                  onChange={(e) =>
                    setRoleForm({
                      ...roleForm,
                      roleDescription: e.target.value,
                    })
                  }
                  placeholder="Describe what this role is used for"
                />
              </label>
            </div>
            <div className="settings-role-access">
              <h3>Module access</h3>
              <p>
                None: no access. View only: view records. Full access: manage
                supported actions.
              </p>
              <div className="settings-module-access-grid">
                {roleDetailsLoading
                  ? ROLE_MODULES.map((module) => (
                      <div
                        className="settings-module-access-field"
                        key={module.label}
                        aria-hidden="true"
                      >
                        <Skeleton width="42%" height={14} />
                        <Skeleton height={40} radius={10} />
                      </div>
                    ))
                  : roleModules.map((module) => (
                      <label
                        className="settings-module-access-field"
                        key={module.label}
                      >
                        <span>{module.label}</span>
                        <ThemedSelect
                          className="settings-role-permission-select"
                          disabled={roleSaving || module.entries.length === 0}
                          displayValue={
                            module.entries.length ? undefined : "Choose access"
                          }
                          menuPortalContainerSelector=".settings-page"
                          value={
                            module.entries.length
                              ? moduleAccessValue(
                                  module.entries.map(
                                    ({ permission }) => permission
                                  )
                                )
                              : ""
                          }
                          aria-label={`${module.label} access`}
                          onChange={(event) => {
                            const indexes = new Set(
                              module.entries.map(({ index }) => index)
                            );
                            const permissions = roleForm.permissions.map(
                              (permission, index) =>
                                indexes.has(index)
                                  ? {
                                      ...permission,
                                      screenRight: event.target.value,
                                    }
                                  : permission
                            );
                            setRoleForm({ ...roleForm, permissions });
                          }}
                        >
                          <option value="" hidden>
                            Choose access
                          </option>
                          {ACCESS_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </ThemedSelect>
                      </label>
                    ))}
              </div>
            </div>
          </fieldset>
          <footer className="settings-role-actions">
            {formError && (
              <div className="settings-form-alert" role="alert">
                {formError}
              </div>
            )}
            <button
              type="button"
              className="settings-button secondary"
              onClick={() => openList("roles")}
              disabled={roleSaving || roleDetailsLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="settings-button primary"
              onClick={saveRole}
              disabled={roleSaving || roleDetailsLoading}
            >
              {roleSaving && (
                <LuLoaderCircle
                  className="settings-role-saving"
                  aria-hidden="true"
                />
              )}
              {roleSaving
                ? selectedRole
                  ? "Saving…"
                  : "Creating…"
                : selectedRole
                ? "Save changes"
                : "Create role"}
            </button>
          </footer>
        </section>
      )}

      <DeleteConfirmationDialog
        open={Boolean(confirm)}
        loading={confirm === "role" && roleDeleting}
        loadingLabel={
          confirm === "role" ? (
            <>
              <LuLoaderCircle
                className="settings-role-saving"
                aria-hidden="true"
              />
              Deleting...
            </>
          ) : undefined
        }
        mobileSheet
        title={confirm === "role" ? "Delete custom role" : "Remove member"}
        entityName={confirm === "role" ? "role" : "member"}
        message={
          confirm === "role"
            ? `Delete ${
                selectedRole?.roleName || "this role"
              }? This action can’t be undone.`
            : `Remove ${getName(
                selectedMember
              )} from this workspace? They will lose access immediately.`
        }
        confirmLabel={confirm === "role" ? "Delete role" : "Remove member"}
        handleCancel={() => setConfirm(null)}
        closeModalClick={() => setConfirm(null)}
        handleDelete={confirm === "role" ? removeRole : removeMember}
      />
    </div>
  );
}
